import {
  CustomerOrderStatus,
  OrderItemStatus,
  OrderStatus,
  type OrderItem,
} from '@/lib/shared-types';

/**
 * §15 / §38 — the customer-visible status is a PROJECTION of internal state, never a second
 * source of truth. Item states drive the overall status so a half-ready order reads as
 * PARTIALLY_READY rather than jumping to READY.
 */
export function toCustomerStatus(
  internal: OrderStatus,
  items: Pick<OrderItem, 'status'>[] = [],
): CustomerOrderStatus {
  switch (internal) {
    case OrderStatus.DRAFT:
      return CustomerOrderStatus.ORDER_RECEIVED;
    case OrderStatus.CONFIRMED:
      return CustomerOrderStatus.CONFIRMED;
    case OrderStatus.SENT_TO_KITCHEN:
    case OrderStatus.PREPARING: {
      if (!items.length) return CustomerOrderStatus.PREPARING;
      // A cancelled item is never coming, so it must not hold the order on PARTIALLY_READY —
      // it leaves the denominator entirely.
      const pending = items.filter((i) => i.status !== OrderItemStatus.CANCELLED);
      if (!pending.length) return CustomerOrderStatus.PREPARING;
      const done = pending.filter((i) =>
        (
          [
            OrderItemStatus.READY,
            OrderItemStatus.AWAITING_PICKUP,
            OrderItemStatus.PICKED_UP,
            OrderItemStatus.SERVED,
          ] as OrderItemStatus[]
        ).includes(i.status),
      ).length;
      if (done === 0) return CustomerOrderStatus.PREPARING;
      if (done < pending.length) return CustomerOrderStatus.PARTIALLY_READY;
      return CustomerOrderStatus.READY;
    }
    case OrderStatus.READY:
      return CustomerOrderStatus.READY;
    case OrderStatus.SERVED:
      return CustomerOrderStatus.SERVED;
    case OrderStatus.COMPLETED:
      return CustomerOrderStatus.COMPLETED;
    default:
      return CustomerOrderStatus.ORDER_RECEIVED;
  }
}

/**
 * §15 — the guest's food, as one list, most recent round first.
 *
 * A guest sees one card per `Order` today — a row that exists only because a waiter or a phone
 * pressed *send*, so "where is my coffee?" is answered with two cards, two progress bars and two
 * status pills in a shape borrowed from the kitchen's bookkeeping. Flattening drops the wrapper
 * and keeps the line, which is the thing whose state actually changes.
 *
 * Three rules the shape has to respect:
 *
 *  1. **Lines are never merged across orders.** Two lattes ordered an hour apart are in
 *     genuinely different states; `2 x Latte` with one status states something false about half
 *     the quantity.
 *  2. **The order number stays on the line.** It is what a guest says out loud to a waiter and
 *     what feedback and re-ordering anchor to; it becomes a caption, not nothing.
 *  3. **Cancelled lines are kept, not dropped.** A guest needs to see that the thing is not
 *     coming; silently removing the row reads as the app having lost it.
 *
 * Callers must still honour `showItemStatus`: with item status hidden there IS no per-line state
 * to show, so only the order-level grouping is left. See `canFlattenGuestOrders`.
 */
export interface GuestLine<TItem, TOrder> {
  item: TItem;
  order: TOrder;
  /** Null when the line was cancelled — it is shown, struck through, not hidden. */
  status: CustomerOrderStatus | null;
}

export interface GuestFlattenableOrder {
  items?: Pick<OrderItem, 'status'>[] | null;
  createdAt?: string | Date | null;
}

/** The element type of an order's `items`, so callers keep their own item type. */
type ItemOf<TOrder extends GuestFlattenableOrder> = NonNullable<TOrder['items']>[number];

export function itemCustomerStatus(status: OrderItemStatus): CustomerOrderStatus | null {
  switch (status) {
    case OrderItemStatus.CANCELLED:
      return null;
    case OrderItemStatus.PENDING:
      return CustomerOrderStatus.ORDER_RECEIVED;
    case OrderItemStatus.QUEUED:
      return CustomerOrderStatus.CONFIRMED;
    case OrderItemStatus.PREPARING:
      return CustomerOrderStatus.PREPARING;
    case OrderItemStatus.READY:
    case OrderItemStatus.AWAITING_PICKUP:
      return CustomerOrderStatus.READY;
    case OrderItemStatus.PICKED_UP:
      return CustomerOrderStatus.PICKED_UP;
    case OrderItemStatus.SERVED:
      return CustomerOrderStatus.SERVED;
    default:
      return CustomerOrderStatus.ORDER_RECEIVED;
  }
}

/**
 * Whether the flat list can say anything. Without per-item status every line is a name and a
 * price with no state, which is strictly worse than the cards it replaced.
 */
export function canFlattenGuestOrders(showItemStatus: boolean): boolean {
  return showItemStatus;
}

/**
 * The one definition of the order a guest reads their orders in: **newest first**.
 *
 * A guest opens the tracking screen because of the round they just placed, not the one they
 * finished an hour ago. Oldest-first pushed the answer to the bottom of a growing list, so by
 * the third round the thing they came to see was off-screen.
 *
 * Two edges, both decided here rather than at each call site:
 *
 *  - **An order with no usable `createdAt` sorts last, never first.** It is a malformed payload,
 *    not a fresh order, and the top row is a claim — "this is your most recent" — that an
 *    undatable row has no standing to make. A missing timestamp and an unparseable one are the
 *    same case; letting `NaN` reach the comparator would break its own contract and scramble the
 *    list around it.
 *  - **Ties keep the order they arrived in.** `Array.prototype.sort` is stable, so two orders
 *    sent in the same second stay in the sequence the API returned rather than shuffling between
 *    repaints.
 *
 * Pure: the input array is never reordered in place.
 *
 * The constraint names `items` even though only `createdAt` is read, and that is deliberate:
 * `Order` in `src/lib/shared-types` does not declare `createdAt` at all — the API sends it,
 * `findAllForGuest` orders by it, and every guest order carries it, but the model is silent. A
 * constraint of `{ createdAt?: ... }` alone is therefore a *weak type* with no property in
 * common with `Order`, and TypeScript rejects every real order in the codebase. Sharing
 * `GuestFlattenableOrder` with `toGuestLines` is what makes the two agree on one shape as well
 * as one rule. Worth declaring `createdAt` on `Order` and narrowing this later.
 */
export function sortGuestOrdersNewestFirst<TOrder extends GuestFlattenableOrder>(
  orders: readonly TOrder[],
): TOrder[] {
  const time = (o: TOrder) => {
    if (!o.createdAt) return 0;
    const ms = new Date(o.createdAt).getTime();
    return Number.isFinite(ms) ? ms : 0;
  };
  return [...orders].sort((a, b) => time(b) - time(a));
}

/**
 * Flattens orders to lines, newest order first, item order preserved within each.
 *
 * The reversal is between orders only. The lines INSIDE an order are the round as the guest
 * composed it — a bill read top to bottom — not a timeline, so they are never reversed.
 */
export function toGuestLines<TOrder extends GuestFlattenableOrder>(
  orders: TOrder[],
): GuestLine<ItemOf<TOrder>, TOrder>[] {
  return sortGuestOrdersNewestFirst(orders)
    .flatMap((order) =>
      ((order.items ?? []) as ItemOf<TOrder>[]).map((item) => ({
        item,
        order,
        status: itemCustomerStatus(item.status),
      })),
    );
}
